import * as React from 'react';
import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import spacing from 'src/constants/spacing';
import mediaQueries from 'src/constants/responsive';
import stylesheet from 'src/constants/stylesheet';
import PreviewItem from './PreviewItem';
import { useApi } from 'src/@context/Api';
import * as classNames from 'classnames';
import DropDown from './DropDown';
import Loading from './Loading';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            maxWidth: spacing.ContentMaxWidt,
            margin: '0 auto',
            boxSizing: 'content-box',
            [mediaQueries.Mobile]: {
                padding: '0 20px',
            },
            [mediaQueries.AfterMobile]: {
                padding: '0 60px',
            }
        },
        title: {
            ...stylesheet.TitleH1,
            maxWidth: 550,
            margin: '0 auto',
            textAlign: 'center',
            padding: '1em 0 .4em 0',

        },
        subtitle: {
            maxWidth: 550,
            textAlign: 'center',
            margin: '0 auto',
            paddingBottom: '1.5rem',
        },

        filterContainer: {
            display: 'flex',
            [mediaQueries.Mobile]: {
                flexDirection: 'column',
                maxWidth: 260,
                margin: '0 auto',
            },
            [mediaQueries.TabletPortrait]: {
                flexDirection: 'column',
                maxWidth: 400,
                margin: '0 auto',
            },
            [mediaQueries.TabletLandScape]: {
                justifyContent: 'center',
                marginBottom: '2rem',
                marginLeft: -spacing.Gutter.Tablet / 2,
                marginRight: -spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.AfterTablet]: {
                justifyContent: 'center',
                marginBottom: '2rem',
            },
            [mediaQueries.Laptop]: {
                justifyContent: 'center',
                marginBottom: '2rem',
                marginLeft: -spacing.Gutter.Laptop / 2,
                marginRight: -spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                justifyContent: 'center',
                marginBottom: '2rem',
                marginLeft: -spacing.Gutter.Desktop / 2,
                marginRight: -spacing.Gutter.Desktop / 2,
            },
        },
        filterElement: {
            [mediaQueries.BeforeTablet]: {
                flex: '0 0 100%',
                width: '100%',
                marginBottom: '1.5rem',
            },
            [mediaQueries.TabletPortrait]: {
                paddingLeft: spacing.Gutter.Tablet / 2,
                paddingRight: spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                paddingLeft: spacing.Gutter.Laptop / 2,
                paddingRight: spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                paddingLeft: spacing.Gutter.Desktop / 2,
                paddingRight: spacing.Gutter.Desktop / 2,
            },
        },

        categoryFilter: {
            [mediaQueries.BeforeTablet]: {
                zIndex: 5
            },
            [mediaQueries.TabletLandScape]: {
                flex: '0 0 200px',
                width: 200,
            },
            [mediaQueries.AfterTablet]: {
                flex: '0 0 200px',
                width: 200,
            },
        },

        tagsFilter: {
            [mediaQueries.BeforeTablet]: {
                zIndex: 1
            },
            [mediaQueries.TabletLandScape]: {
                flex: '0 0 200px',
                width: 200,
            },
            [mediaQueries.AfterTablet]: {
                flex: '0 0 200px',
                width: 200,
            },
        },
        panels: {
            [mediaQueries.Mobile]: {},
            [mediaQueries.AfterMobile]: {
                display: 'flex',
                flexWrap: 'wrap',
            },
            [mediaQueries.Tablet]: {
                marginLeft: -spacing.Gutter.Tablet / 2,
                marginRight: -spacing.Gutter.Tablet / 2,
            },
            [mediaQueries.Laptop]: {
                marginLeft: -spacing.Gutter.Laptop / 2,
                marginRight: -spacing.Gutter.Laptop / 2,
            },
            [mediaQueries.Desktop]: {
                marginLeft: -spacing.Gutter.Desktop / 2,
                marginRight: -spacing.Gutter.Desktop / 2,
            },
        },
        panel: {
            boxSizing: 'border-box',
            [mediaQueries.Mobile]: {
                width: '100%',
                marginBottom: spacing.Gutter.Mobile * 2,
            },
            [mediaQueries.Tablet]: {
                paddingLeft: spacing.Gutter.Tablet / 2,
                paddingRight: spacing.Gutter.Tablet / 2,
                marginBottom: spacing.Gutter.Tablet * 2,
            },
            [mediaQueries.TabletPortrait]: {
                width: `${1 / 2 * 100}%`,
            },
            [mediaQueries.TabletLandScape]: {
                width: `${1 / 3 * 100}%`,
            },
            [mediaQueries.AfterTablet]: {
                width: `${1 / 3 * 100}%`,
            },
            [mediaQueries.Laptop]: {
                paddingLeft: spacing.Gutter.Laptop / 2,
                paddingRight: spacing.Gutter.Laptop / 2,
                marginBottom: spacing.Gutter.Laptop * 2,
            },
            [mediaQueries.Desktop]: {
                paddingLeft: spacing.Gutter.Desktop / 2,
                paddingRight: spacing.Gutter.Desktop / 2,
                marginBottom: spacing.Gutter.Desktop * 2,
            },
        },
    }));

export type DownloadProps = DownloadDto;
type Props = DownloadProps;

const Download: FC<Props> = ({ Title, DownloadCategories, UID, Tags }) => {
    const cls = useStyles();

    const itemsToLoad = 9;
    const [hasMore, setHasMore] = useState<boolean>();
    const [count, setCount] = useState(0);

    const { fetchDownloadItems, app } = useApi();
    const [downloadItems, setDownloadItems] = useState<DownloadItemDto[]>([]);
    const [category, setCategory] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const config = app && app.Config;
    const translationsItems = config && config.TranslationItems;
    const categoryTranslation = translationsItems && translationsItems.find(t => t.UID === 'download_category');
    const tagsTranslation = translationsItems && translationsItems.find(t => t.UID === 'tags');

    const laodItems = async () => {
        if (loading) { return }
        setLoading(true);

        const response = await fetchDownloadItems(UID, count, itemsToLoad, category, tags);

        setDownloadItems(prevItems => {
            return [...prevItems, ...response]
        });

        setHasMore(response.length === itemsToLoad);
        setLoading(false);
    };

    useEffect(() => {
        if (loading) { return }
        laodItems();
    }, [category, count, tags]);

    const onTagsChange = (tag: string) => {
        if (tag === "") {
            setTags(['']);
            return;
        }

        if (tags && tags.indexOf(tag) > -1) {

            // const result = tags.filter((t, i) => i !== tags.indexOf(tag));
            const result = tags.splice(tags.indexOf(tag), 1);

            // TODO: Why does this not trigger useEffect? Something about mutable and imutable object?
            // tags.splice(tags.indexOf(tag), 1);

            setTags(result);
        } else {
            setTags([...tags, tag])
        }
    }

    const options: IntersectionObserverInit = { threshold: 1 }
    const observer = useRef<IntersectionObserver>();
    const lastItemLoaded = useCallback((item) => {
        if (loading) { return }

        if (observer.current) { observer.current.disconnect() }

        observer.current = new IntersectionObserver(items => {
            if (items[0].isIntersecting && hasMore) {
                setCount(prevCount => prevCount + itemsToLoad);
            }
        }, options)

        if (item) {
            observer.current.observe(item);
        }
    }, [loading]);

    return (
        <section className={cls.root}>
            {Title &&
                <h1 className={cls.title}>{Title}</h1>
            }
            <div className={cls.filterContainer}>
                {downloadItems &&
                    <div className={classNames(cls.filterElement, cls.categoryFilter)}>
                        <DropDown
                            title={categoryTranslation && categoryTranslation.Translation || ''}
                            options={DownloadCategories.map(t => ({ title: t.Title, value: t.ObjectId }))}
                            isTags={false}
                            // tslint:disable-next-line:jsx-no-lambda
                            onChanged={(item) => {
                                setCategory(item && item.value || '');
                                setHasMore(false);
                                setCount(0);
                                setDownloadItems([])
                            }}
                        />
                    </div>
                }
                {
                    Tags &&
                    <div className={classNames(cls.filterElement, cls.tagsFilter)}>
                        <DropDown
                            title={tagsTranslation && tagsTranslation.Translation || ''}
                            options={Tags.map(t => ({ title: t.Title, value: t.ObjectId }))}
                            isTags={true}
                            // tslint:disable-next-line:jsx-no-lambda
                            onChanged={(item) => {
                                onTagsChange(item && item.value || '');
                                setCount(0);
                                setDownloadItems([]);
                            }}
                        />
                    </div>
                }
            </div>
            {downloadItems &&
                <article className={cls.panels}>
                    {downloadItems.map((donwloadItem, i) => {

                        if (downloadItems.length === i + 1) {
                            return (<div
                                key={`product-${donwloadItem.UID}`}
                                ref={lastItemLoaded}
                                className={cls.panel}
                            >
                                <PreviewItem previewItem={donwloadItem} />
                            </div>);
                        } else {
                            return (<div
                                key={`product-${donwloadItem.UID}`}
                                className={cls.panel}
                            >
                                <PreviewItem previewItem={donwloadItem} />
                            </div>);
                        }

                    })}
                </article>
            }
            {loading && <Loading />}
        </section>
    );
}

export default Download;


// WEBPACK FOOTER //
// ./src/Components/Download.tsx